import { useState } from 'react'
import { Upload, FileText, CheckCircle, AlertCircle, Loader, X } from 'lucide-react'
import { apiRequest } from '@/config/api'

interface ParsedContactInfo {
  email?: string
  phone?: string
  linkedin_url?: string
  github_url?: string
}

interface ParsedCandidateData {
  email?: string
  phone?: string
  linkedin_url?: string
  github_url?: string
  current_title?: string
  current_company?: string
  summary?: string
  skills?: string[]
}

interface ResumeUploadWidgetProps {
  onDataParsed: (data: ParsedCandidateData) => void
  onError: (error: string) => void
  className?: string
  mode?: 'contact-only' | 'full-parse'  // contact-only for quick autofill, full-parse for complete data
}

export default function ResumeUploadWidget({ 
  onDataParsed, 
  onError, 
  className = '',
  mode = 'contact-only'
}: ResumeUploadWidgetProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [parseSuccess, setParseSuccess] = useState(false)

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = ['.pdf', '.docx', '.doc', '.txt']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    
    if (!allowedTypes.includes(fileExtension)) {
      onError(`Unsupported file type. Please upload: ${allowedTypes.join(', ')}`)
      return
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      onError('File size too large. Maximum allowed size is 10MB.')
      return
    }

    setUploadedFile(file)
    parseResume(file)
  }

  const parseResume = async (file: File) => {
    setIsUploading(true)
    setParseSuccess(false)

    try {
      const formData = new FormData()
      formData.append('file', file)

      // Choose endpoint based on mode
      const endpoint = mode === 'contact-only' 
        ? 'resume-parser/extract-contact-info'
        : 'resume-parser/upload'

      const response = await apiRequest(endpoint, {
        method: 'POST',
        body: formData,
        headers: {} // Don't set Content-Type for FormData - JWT token will be added by apiRequest
      })

      const data = await response.json()

      if (data.success) {
        setParseSuccess(true)
        
        if (mode === 'contact-only') {
          // Return just contact info
          const contactInfo = data.data.contact_info || {}
          onDataParsed(contactInfo)
        } else {
          // Return full candidate data
          const candidateData = data.data.candidate_data || {}
          
          // Parse skills from JSON string if needed
          if (typeof candidateData.skills === 'string') {
            try {
              candidateData.skills = JSON.parse(candidateData.skills)
            } catch {
              candidateData.skills = []
            }
          }
          
          onDataParsed(candidateData)
        }
      } else {
        onError(data.message || 'Resume parsing failed')
      }
    } catch (error: any) {
      onError('Failed to parse resume. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const clearFile = () => {
    setUploadedFile(null)
    setParseSuccess(false)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
    // Clear the input to allow re-selecting the same file
    e.target.value = ''
  }

  return (
    <div className={`resume-upload-widget ${className}`}>
      {!uploadedFile ? (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragOver 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="flex flex-col items-center space-y-3">
            <Upload className="w-8 h-8 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-700">
                {mode === 'contact-only' 
                  ? 'Upload resume to auto-fill contact info'
                  : 'Upload resume to auto-fill application form'
                }
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports PDF, DOCX, DOC, TXT files (max 10MB)
              </p>
            </div>
            <label className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
              Choose File
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                onChange={handleFileInput}
                className="hidden"
              />
            </label>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {uploadedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {Math.round(uploadedFile.size / 1024)} KB
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {isUploading && (
                <div className="flex items-center space-x-2">
                  <Loader className="w-4 h-4 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-600">Parsing...</span>
                </div>
              )}
              
              {parseSuccess && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600">
                    {mode === 'contact-only' ? 'Contact info extracted' : 'Resume parsed'}
                  </span>
                </div>
              )}
              
              <button
                onClick={clearFile}
                className="text-gray-400 hover:text-gray-600"
                disabled={isUploading}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {parseSuccess && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-700">
                {mode === 'contact-only' 
                  ? '✓ Contact information has been extracted and filled in the form'
                  : '✓ Resume data has been extracted and filled in the application form'
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}